import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["tab", "content"]
  static values = { current: String }

  connect() {
    this.updateTabState()
    
    // Listen for turbo frame navigation
    this.element.addEventListener('turbo:before-frame-render', this.handleFrameNavigation.bind(this))
    
    // Also listen for URL changes (browser back/forward)
    window.addEventListener('popstate', this.handlePopState.bind(this))
    
    // Listen for turbo frame completion to hide loading state
    this.element.addEventListener('turbo:frame-load', this.hideLoadingState.bind(this))
  }
  
  disconnect() {
    this.element.removeEventListener('turbo:before-frame-render', this.handleFrameNavigation.bind(this))
    window.removeEventListener('popstate', this.handlePopState.bind(this))
    this.element.removeEventListener('turbo:frame-load', this.hideLoadingState.bind(this))
  }
  
  handleFrameNavigation(event) {
    // Extract subsection from the URL that's being navigated to
    const url = new URL(event.detail.newFrame.baseURI)
    const subsection = url.searchParams.get('subsection')
    
    if (subsection && subsection !== this.currentValue) {
      this.currentValue = subsection
      this.updateTabState()
    }
  }
  
  handlePopState(event) {
    // Handle browser back/forward navigation
    const url = new URL(window.location)
    const subsection = url.searchParams.get('subsection') || 'overview'
    
    if (subsection !== this.currentValue) {
      this.currentValue = subsection
      this.updateTabState()
    }
  }

  switchTab(event) {
    const clickedTab = event.currentTarget
    const subsection = clickedTab.dataset.subsection
    
    // Don't switch if already active
    if (this.currentValue === subsection) {
      event.preventDefault()
      return
    }

    // Show loading state immediately when user clicks
    this.showLoadingState()

    // Update current value immediately for visual feedback
    this.currentValue = subsection
    
    // Update tab appearance immediately
    this.updateTabState()
    
    // Update URL without full page reload
    const url = new URL(window.location)
    url.searchParams.set('subsection', subsection)
    window.history.replaceState({}, '', url)
    
    // Let Turbo Frame handle the content loading naturally
    // No need to preventDefault - Turbo will handle it
  }

  updateTabState() {
    this.tabTargets.forEach(tab => {
      const subsection = tab.dataset.subsection
      const isActive = subsection === this.currentValue

      // Update tab styling
      if (isActive) {
        tab.classList.remove('border-transparent', 'text-gray-500', 'hover:text-gray-700', 'hover:border-gray-300')
        tab.classList.add('border-black', 'text-black')
        
        // Update icon color
        const icon = tab.querySelector('svg')
        if (icon) {
          icon.classList.remove('text-gray-400', 'group-hover:text-gray-500')
          icon.classList.add('text-black')
        }
      } else {
        tab.classList.remove('border-black', 'text-black')
        tab.classList.add('border-transparent', 'text-gray-500', 'hover:text-gray-700', 'hover:border-gray-300')
        
        // Update icon color
        const icon = tab.querySelector('svg')
        if (icon) {
          icon.classList.remove('text-black')
          icon.classList.add('text-gray-400', 'group-hover:text-gray-500')
        }
      }
    })
  }

  // Handle successful content loading
  contentLoaded() {
    // Re-initialize any controllers that might be in the new content
    this.application.start()
  }
  
  showLoadingState() {
    const billingContent = document.getElementById('billing_content')
    if (billingContent) {
      // Show skeleton based on current tab
      const skeletonHtml = this.getSkeletonForTab(this.currentValue)
      billingContent.innerHTML = skeletonHtml
      billingContent.classList.add('loading-blur')
      
      // Failsafe: Remove loading state after 3 seconds if stuck
      if (this.loadingTimeout) {
        clearTimeout(this.loadingTimeout)
      }
      this.loadingTimeout = setTimeout(() => {
        this.hideLoadingState()
      }, 3000)
    }
  }
  
  hideLoadingState() {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout)
      this.loadingTimeout = null
    }
    
    const billingContent = document.getElementById('billing_content')
    if (billingContent) {
      billingContent.classList.remove('loading-blur')
    }
  }
  
  getSkeletonForTab(tabName) {
    switch(tabName) {
      case 'overview':
        return this.getOverviewSkeleton()
      case 'payment_methods':
        return this.getPaymentMethodsSkeleton()
      case 'billing_history':
        return this.getBillingHistorySkeleton()
      case 'preferences':
        return this.getPreferencesSkeleton()
      default:
        return this.getGenericSkeleton()
    }
  }
  
  getOverviewSkeleton() {
    return `
      <div class="space-y-6">
        <!-- Usage Stats Card -->
        <div class="bg-white rounded-lg border border-gray-200 p-6">
          <div class="skeleton-text w-48 mb-4"></div>
          <div class="skeleton-text-short mb-2"></div>
          <div class="skeleton-card mb-4"></div>
        </div>
        
        <!-- Quick Actions Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div class="skeleton-card"></div>
          <div class="skeleton-card"></div>
          <div class="skeleton-card"></div>
        </div>
        
        <!-- How It Works -->
        <div class="bg-gray-50 rounded-lg p-4">
          <div class="skeleton-text w-40 mb-3"></div>
          <div class="space-y-2">
            <div class="skeleton-text-short"></div>
            <div class="skeleton-text-short"></div>
            <div class="skeleton-text-short"></div>
          </div>
        </div>
      </div>
    `
  }
  
  getPaymentMethodsSkeleton() {
    return `
      <div class="space-y-6">
        <div class="bg-white rounded-lg border border-gray-200">
          <div class="px-4 py-5 sm:p-6">
            <div class="skeleton-text w-48 mb-4"></div>
            <div class="space-y-3">
              <div class="skeleton-card"></div>
            </div>
          </div>
        </div>
        <div class="mt-4">
          <div class="skeleton-button"></div>
        </div>
      </div>
    `
  }
  
  getBillingHistorySkeleton() {
    return `
      <div class="space-y-6">
        <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div class="px-4 py-5 sm:p-6">
            <div class="skeleton-text w-48 mb-4"></div>
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th class="px-4 py-3"><div class="skeleton-text w-16"></div></th>
                    <th class="px-4 py-3"><div class="skeleton-text w-16"></div></th>
                    <th class="px-4 py-3"><div class="skeleton-text w-20"></div></th>
                    <th class="px-4 py-3"><div class="skeleton-text w-16"></div></th>
                    <th class="px-4 py-3"><div class="skeleton-text w-16"></div></th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200">
                  <tr><td class="px-4 py-4" colspan="5"><div class="skeleton-text"></div></td></tr>
                  <tr><td class="px-4 py-4" colspan="5"><div class="skeleton-text"></div></td></tr>
                  <tr><td class="px-4 py-4" colspan="5"><div class="skeleton-text"></div></td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    `
  }
  
  getPreferencesSkeleton() {
    return `
      <div class="space-y-6">
        <div class="bg-white rounded-lg border border-gray-200">
          <div class="px-4 py-5 sm:p-6">
            <div class="skeleton-text w-48 mb-4"></div>
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <div class="skeleton-text w-32 mb-2"></div>
                <div class="skeleton-card h-10"></div>
              </div>
              <div>
                <div class="skeleton-text w-32 mb-2"></div>
                <div class="skeleton-card h-10"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `
  }
  
  getGenericSkeleton() {
    return `
      <div class="space-y-6">
        <div class="bg-white rounded-lg border border-gray-200 p-6">
          <div class="skeleton-text w-48 mb-4"></div>
          <div class="skeleton-text-short mb-2"></div>
          <div class="skeleton-card"></div>
        </div>
      </div>
    `
  }
}